import React, { useRef, useEffect, useState } from 'react';
import { Comment } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { usePermissions } from 'utils/usePermissions';
import { COMMENT_CREATE } from 'utils/access-control/rules';

import { Spinner } from 'components/Spinner';
import { AddComment } from './AddComment';
import { EditComment } from './EditComment';
import { DeleteComment } from './DeleteComment';
import { UserAvatar } from 'components/UserAvatar';

const StyledCommentsWrapper = styled.div`
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  display: ${props => props.columnLayout && 'flex'};

  .ant-comment-content-author-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-comment-actions {
    margin: 0.5rem 0 0 0;
  }

  .ant-comment-inner {
    padding: 0.5rem 0;
  }

  .ant-comment-content-detail {
    white-space: break-spaces;
  }

  // to fix the styling in Firefox as a result of the white-space property
  .ant-row::before,
  .ant-row::after,
  .ant-legacy-form-item-control::before,
  .ant-legacy-form-item-control::after {
    display: unset;
    content: unset;
  }

  ${props =>
    !props.isAtTop &&
    `
    &:before {
      content: '';
      position: absolute;
      top: -10px;
      left: 0;
      right: 0;
      height: 10px;
      box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2);
      z-index: 1;
    }
  `}
`;

const StyledSpinner = styled(Spinner)`
  min-height: unset;
`;

function FromNow({ timestamp }) {
  const time = moment(timestamp);
  return <span title={time.format('lll')}>{time.fromNow()}</span>;
}

export const Comments = ({
  comments = [],
  className,
  onSave,
  onEdit,
  editId,
  saveLoading,
  editLoading,
  loading,
  columnLayout,
  createRule = [COMMENT_CREATE],
  areWriteActionsVisible = true,
}) => {
  const { CanRender } = usePermissions();
  const commentsRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [commentsLength, setCommentsLength] = useState(0);

  useEffect(() => {
    const commentsNode = commentsRef.current;
    // Check if the comments length has increased
    if (commentsNode && comments.length > commentsLength) {
      commentsNode.scrollTop = commentsNode.scrollHeight;
    }
    setCommentsLength(comments.length);
    const scrollListener = () => {
      if (commentsNode.scrollTop === 0) {
        setIsAtTop(true);
      } else {
        setIsAtTop(false);
      }
    };
    commentsNode.addEventListener('scroll', scrollListener);
    return () => {
      commentsNode.removeEventListener('scroll', scrollListener);
    };
  }, [comments, commentsLength]);

  const getActions = ({ id, canDelete, canUpdate, comment }) =>
    [
      canDelete && (
        <DeleteComment
          id={id}
          hideDeleteIcon
          type="link"
          deleteBtnCss={{
            fontSize: '0.75rem !important',
            paddingLeft: '0 !important',
            height: 'unset !important',
          }}
        />
      ),
      canUpdate && <EditComment id={id} comment={comment} onEdit={onEdit} loading={editLoading} />,
    ].filter(Boolean);

  return (
    <StyledCommentsWrapper className={className} columnLayout={columnLayout} isAtTop={isAtTop}>
      <div data-testid="comments" ref={commentsRef}>
        {loading ? (
          <StyledSpinner />
        ) : comments.length ? (
          comments.map(
            ({
              id,
              permissions: { canDelete, canUpdate } = {},
              comment,
              timestamp,
              author = {},
            }) => {
              const title = author.name || author.email || '(Invalid User)';
              return (
                <Comment
                  className={`testid-comment-${id}`}
                  key={id}
                  avatar={<UserAvatar user={author} />}
                  author={<span title={title}>{title}</span>}
                  datetime={FromNow({ timestamp })}
                  actions={
                    areWriteActionsVisible && getActions({ id, canDelete, canUpdate, comment })
                  }
                  content={editLoading && id === editId ? <StyledSpinner /> : comment}
                />
              );
            }
          )
        ) : (
          <em>no comments yet...</em>
        )}
        {saveLoading ? <StyledSpinner /> : null}
      </div>
      <CanRender
        rules={createRule}
        render={() =>
          areWriteActionsVisible && <AddComment onSave={onSave} columnLayout={columnLayout} />
        }
      />
    </StyledCommentsWrapper>
  );
};
